import React, { useEffect, useRef, useState, useCallback } from "react";
import { adminApi } from "@/lib/adminApi";
import { HeroSlideshow } from "@/components/common/HeroSlideshow";
import { Field } from "@/pages/admin/Field";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Trash2, Pencil, GripVertical, ArrowUp, ArrowDown, Replace, Eye } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : "");
const MAX_BYTES = 25 * 1024 * 1024;
const RESIZE_HINT_BYTES = 10 * 1024 * 1024;

const validateFile = (file) => {
  if (!["image/png", "image/jpeg"].includes(file.type)) return "Only PNG or JPG/JPEG images are allowed";
  if (file.size > MAX_BYTES) return "Image must be 25 MB or smaller";
  return null;
};

export const SlideshowSection = () => {
  const [images, setImages] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [seconds, setSeconds] = useState(3);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [editing, setEditing] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [previewKey, setPreviewKey] = useState(0);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    adminApi.slideshow().then((d) => {
      setImages(d.images || []);
      setEnabled(d.settings?.enabled !== false);
      setSeconds(d.settings?.seconds || 3);
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const refreshPreview = () => setPreviewKey((k) => k + 1);

  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const err = validateFile(file);
    if (err) { toast.error(err); return; }
    if (images.length >= 10) { toast.error("You can keep at most 10 images. Delete one first."); return; }
    setUploading(true); setProgress(0);
    try {
      await adminApi.uploadSlide(file, (p) => setProgress(Math.round((p.loaded / (p.total || p.loaded)) * 100)));
      toast.success("Uploaded. Add alt text, then turn it on.");
      load();
    } catch (err2) { toast.error(err2?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); setProgress(0); }
  };

  const toggleActive = async (im) => {
    if (!im.active && !im.alt_en) { toast.error("Add English alt text before showing this image"); setEditing(im); return; }
    try { await adminApi.updateSlide(im.id, { active: !im.active }); load(); refreshPreview(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (im) => {
    if (!window.confirm("Delete this image?")) return;
    await adminApi.deleteSlide(im.id); toast.success("Deleted"); load(); refreshPreview();
  };

  const persistOrder = async (arr) => {
    setImages(arr);
    await adminApi.reorderSlides(arr.map((x) => x.id)); refreshPreview();
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= images.length) return;
    const arr = [...images];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    persistOrder(arr);
  };
  const onDrop = (i) => {
    if (dragIdx === null || dragIdx === i) return;
    const arr = [...images];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(i, 0, moved);
    setDragIdx(null);
    persistOrder(arr);
  };

  const saveSettings = async () => {
    try { await adminApi.slideSettings({ enabled, seconds }); toast.success("Settings saved"); refreshPreview(); }
    catch { toast.error("Save failed"); }
  };

  return (
    <div className="space-y-10">
      <div>
        <h2 className="mb-2 font-serif text-2xl font-semibold text-gmoaa-green">Home slideshow</h2>
        <p className="mb-5 text-sm text-gmoaa-text-muted">Up to 10 images on the home hero. Recommended size 1600 × 1000 px (16:10). PNG or JPG/JPEG, up to 25 MB each — large images are resized automatically.</p>

        {/* Settings */}
        <div className="mb-6 space-y-4 rounded-xl border border-gmoaa-border-subtle bg-white p-5">
          <div className="flex items-center gap-2">
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="slideshow-enabled" />
            <Label className="text-sm">Show the slideshow on the home page</Label>
          </div>
          <div className="max-w-sm">
            <Label className="mb-2 block text-xs font-medium text-gmoaa-text-muted">Seconds per image: {seconds}s</Label>
            <Slider min={2} max={10} step={1} value={[seconds]} onValueChange={(v) => setSeconds(v[0])} data-testid="slideshow-seconds" />
          </div>
          <Button onClick={saveSettings} data-testid="slideshow-settings-save" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save settings</Button>
        </div>

        {/* Upload */}
        <div className="mb-6 space-y-3 rounded-xl border border-dashed border-gmoaa-border-strong bg-white p-5">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gmoaa-green bg-white px-3 py-2 text-sm font-medium text-gmoaa-green hover:bg-gmoaa-green-light">
            <Upload size={15} /> {uploading ? `Uploading… ${progress}%` : "Upload image"}
            <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={onFile} disabled={uploading} data-testid="slideshow-upload-input" />
          </label>
          {uploading && (
            <div className="h-2 w-full overflow-hidden rounded-full bg-gmoaa-green-light">
              <div className="h-full bg-gmoaa-green transition-all" style={{ width: `${progress}%` }} data-testid="slideshow-upload-progress" />
            </div>
          )}
          <p className="text-xs text-gmoaa-text-muted">Images over 10 MB will be resized automatically. {images.length}/10 used.</p>
        </div>

        {/* Image list */}
        <div className="space-y-2" data-testid="slideshow-list">
          {images.map((im, i) => (
            <div
              key={im.id}
              draggable
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(i)}
              className="flex items-center gap-3 rounded-xl border border-gmoaa-border-subtle bg-white p-3"
              data-testid={`slide-row-${i}`}
            >
              <GripVertical size={16} className="cursor-grab text-gmoaa-text-muted" />
              <div className="h-14 w-24 shrink-0 overflow-hidden rounded border border-gmoaa-border-subtle bg-gmoaa-surface">
                {im.thumb ? <img src={mediaUrl(im.thumb)} alt="" className="h-full w-full object-cover" /> : null}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gmoaa-text-dark">{im.alt_en || <span className="text-gmoaa-red">No alt text yet</span>}</p>
                <p className="truncate text-xs text-gmoaa-text-muted">{im.caption_en || "—"}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Switch checked={!!im.active} onCheckedChange={() => toggleActive(im)} data-testid={`slide-active-${i}`} />
                <Button size="sm" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp size={15} /></Button>
                <Button size="sm" variant="ghost" onClick={() => move(i, 1)} disabled={i === images.length - 1}><ArrowDown size={15} /></Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(im)} data-testid={`slide-edit-${i}`}><Pencil size={15} /></Button>
                <Button size="sm" variant="ghost" className="text-gmoaa-red" onClick={() => del(im)} data-testid={`slide-del-${i}`}><Trash2 size={15} /></Button>
              </div>
            </div>
          ))}
          {images.length === 0 && <p className="text-sm text-gmoaa-text-muted">No images yet. Upload one to start.</p>}
        </div>
      </div>

      {/* Live preview */}
      <div>
        <h2 className="mb-4 flex items-center gap-2 font-serif text-2xl font-semibold text-gmoaa-green"><Eye size={18} /> Live preview</h2>
        <div className="max-w-xl" key={previewKey}>
          <HeroSlideshow />
        </div>
      </div>

      {editing && <EditDialog im={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); refreshPreview(); }} />}
    </div>
  );
};

const EditDialog = ({ im, onClose, onSaved }) => {
  const [form, setForm] = useState({
    alt_en: im.alt_en || "", alt_gu: im.alt_gu || "", caption_en: im.caption_en || "", caption_gu: im.caption_gu || "",
    link: im.link || "", cta_label_en: im.cta_label_en || "", cta_label_gu: im.cta_label_gu || "",
    focal_x: im.focal_x ?? 0.5, focal_y: im.focal_y ?? 0.5,
  });
  const [saving, setSaving] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [progress, setProgress] = useState(0);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const onFocalClick = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.min(Math.max((e.clientX - r.left) / r.width, 0), 1);
    const y = Math.min(Math.max((e.clientY - r.top) / r.height, 0), 1);
    setForm((f) => ({ ...f, focal_x: +x.toFixed(3), focal_y: +y.toFixed(3) }));
  };

  const save = async () => {
    if (!form.alt_en.trim()) { toast.error("English alt text is required"); return; }
    setSaving(true);
    try { await adminApi.updateSlide(im.id, form); toast.success("Saved"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const onReplace = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const err = validateFile(file);
    if (err) { toast.error(err); return; }
    setReplacing(true); setProgress(0);
    try { await adminApi.replaceSlide(im.id, file, (p) => setProgress(Math.round((p.loaded / (p.total || p.loaded)) * 100))); toast.success("Image replaced"); onSaved(); }
    catch (e2) { toast.error(e2?.response?.data?.detail || "Replace failed"); }
    finally { setReplacing(false); setProgress(0); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto bg-gmoaa-cream">
        <DialogHeader><DialogTitle className="text-gmoaa-green">Edit slide</DialogTitle></DialogHeader>

        <div className="space-y-3">
          <p className="text-xs font-medium text-gmoaa-text-muted">Focal point — click the image to choose what stays centred when cropped to 16:10.</p>
          <div className="relative w-full overflow-hidden rounded-lg border border-gmoaa-border-subtle" onClick={onFocalClick} style={{ cursor: "crosshair" }} data-testid="slide-focal-canvas">
            {im.preview ? <img src={mediaUrl(im.preview)} alt="" className="w-full object-cover" draggable={false} /> : null}
            <span className="absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-gmoaa-saffron shadow"
              style={{ left: `${form.focal_x * 100}%`, top: `${form.focal_y * 100}%` }} />
          </div>

          <Field label="Alt text (English) — required" value={form.alt_en} onChange={set("alt_en")} testid="slide-alt-en" />
          <Field label="Alt text (Gujarati)" value={form.alt_gu} onChange={set("alt_gu")} />
          <Field label="Caption (English, optional)" value={form.caption_en} onChange={set("caption_en")} />
          <Field label="Caption (Gujarati, optional)" value={form.caption_gu} onChange={set("caption_gu")} />
          <Field label="Link (optional)" value={form.link} onChange={set("link")} placeholder="https://…" />
          <Field label="Button label (English, optional)" value={form.cta_label_en} onChange={set("cta_label_en")} placeholder="e.g. View GAPL hub" testid="slide-cta-en" />
          <Field label="Button label (Gujarati, optional)" value={form.cta_label_gu} onChange={set("cta_label_gu")} />
          <p className="text-xs text-gmoaa-text-muted">Add a link and a button label to show a call-to-action button on the slide.</p>

          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gmoaa-green bg-white px-3 py-2 text-xs font-medium text-gmoaa-green hover:bg-gmoaa-green-light">
            <Replace size={14} /> {replacing ? `Replacing… ${progress}%` : "Replace image"}
            <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={onReplace} disabled={replacing} data-testid="slide-replace-input" />
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-gmoaa-border-subtle">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="slide-save" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
